import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DropDown, InputField, SecondaryButton } from "@financial-edge/shared";
import api from "../api/api";
import AuthAlert from "../components/auth/AuthAlert";
import AuthShell from "../components/auth/AuthShell";
import PrimaryButton from "../components/layout/PrimaryButton";
import { getSignInErrorMessage } from "../utils/auth/authDiagnostics";
import { buildBlockedSignInStatusState, isBlockedSignInInterception } from "../utils/auth/interceptedAccess";
import { buildLaunchPath, buildLaunchSearch, getIntentDetails, getLaunchContext, parseIntent } from "../utils/auth/intent";
import { formatDisplayNumber, parsePhoneInput } from "../utils/auth/authUtils";
import { useAuthPageMeta } from "../utils/ui/useAuthPageMeta";

const RECOVERY_REASONS = [
    { value: "lost_phone_and_email", label: "I lost access to my mobile number" },
    { value: "unable_to_verify_contact", label: "I cannot access my usual sign-in details" },
    { value: "identity_conflict", label: "My sign-in details no longer match my account" },
];

export default function RecoveryRequestPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const launchContext = getLaunchContext(location.search);
    const intent = parseIntent(location.state?.intent || launchContext.intent);
    const source = location.state?.source || launchContext.source;
    const intentDetails = getIntentDetails(intent, source);
    const returnTo = location.state?.returnTo || launchContext.returnTo;
    const [form, setForm] = useState({
        fullName: "",
        claimedMobileNumber: location.state?.phone || "",
        claimedPreviousEmail: location.state?.email || "",
        currentEmail: "",
        companyName: "",
        reason: "lost_phone_and_email",
        notes: "",
    });
    const [state, setState] = useState({
        status: "idle",
        error: "",
        confirmation: null,
    });

    useAuthPageMeta("Account Recovery");

    const canSubmit = useMemo(() => {
        return Boolean(form.fullName.trim() && (form.currentEmail.trim() || form.claimedMobileNumber.trim() || form.claimedPreviousEmail.trim()) && state.status !== "submitting");
    }, [form, state.status]);

    const updateField = (key, value) => {
        setForm(current => ({ ...current, [key]: value }));
        setState(current => ({ ...current, error: "" }));
    };

    const handleSubmit = async (event) => {
        event?.preventDefault?.();
        setState({ status: "submitting", error: "", confirmation: null });

        try {
            const response = await api.submitRecoveryRequest({
                claimedMobileNumber: form.claimedMobileNumber,
                claimedPreviousEmail: form.claimedPreviousEmail,
                currentEmail: form.currentEmail,
                fullName: form.fullName,
                companyName: form.companyName,
                reason: form.reason,
                notes: form.notes,
                sourcePortal: source || "shared-login",
                sourceScreen: "recovery-intake",
                intent,
            });

            if (isBlockedSignInInterception(response?.access)) {
                navigate(`/auth-status${buildLaunchSearch({ intent, source, returnTo, brokerageId: launchContext.brokerageId }) || ""}`, {
                    replace: true,
                    state: buildBlockedSignInStatusState(response.access, {
                        intent,
                    }),
                });
                return;
            }

            setState({
                status: "submitted",
                error: "",
                confirmation: response?.recovery || null,
            });
        } catch (error) {
            setState({
                status: "error",
                error: getSignInErrorMessage(error),
                confirmation: null,
            });
        }
    };

    if (state.status === "submitted") {
        return (
            <AuthShell
                eyebrow="Recovery submitted"
                title="We received your recovery request"
                description={state.confirmation?.confirmationMessage || "A staff member will review your request and follow up using the current contact details you provided."}
                align="center"
            >
                <div className="space-y-4">
                    <AuthAlert tone="success" title="What happens next">
                        Recovery reference: {state.confirmation?.recoveryCase?.recoveryRequestId || "pending"}
                    </AuthAlert>
                    <div className="flex flex-col gap-3">
                        <PrimaryButton onClick={() => navigate(buildLaunchPath("/login", { intent, source, returnTo }))}>
                            Return to sign-in
                        </PrimaryButton>
                        <SecondaryButton type="button" onClick={() => navigate("/entry")}>
                            Choose another workspace
                        </SecondaryButton>
                    </div>
                </div>
            </AuthShell>
        );
    }

    return (
        <AuthShell
            eyebrow="Manual recovery"
            title="Request account recovery help"
            description={`Use this secure form if you can no longer access your verified mobile number for the ${intentDetails.label.toLowerCase()}.`}
            footer={<p className="text-sm text-secondary">We route recovery requests to staff for manual review. Please provide enough detail for us to verify your account safely.</p>}
        >
            <form onSubmit={handleSubmit} noValidate>
                <div className="grid gap-4 md:grid-cols-2">
                    <InputField
                        label="Full name"
                        value={form.fullName}
                        onChange={(value) => updateField("fullName", value)}
                        className="w-full"
                    />
                    <InputField
                        label="Current email"
                        type="email"
                        value={form.currentEmail}
                        onChange={(value) => updateField("currentEmail", value)}
                        className="w-full"
                    />
                    <InputField
                        label="Known mobile number"
                        type="tel"
                        value={formatDisplayNumber(form.claimedMobileNumber)}
                        onChange={(value) => updateField("claimedMobileNumber", parsePhoneInput(value))}
                        className="w-full"
                    />
                    <InputField
                        label="Previous email if known"
                        type="email"
                        value={form.claimedPreviousEmail}
                        onChange={(value) => updateField("claimedPreviousEmail", value)}
                        className="w-full"
                    />
                    <InputField
                        label="Brokerage or company"
                        value={form.companyName}
                        onChange={(value) => updateField("companyName", value)}
                        className="w-full md:col-span-2"
                    />
                </div>

                <div className="mt-5">
                    <DropDown
                        label="Reason for recovery"
                        opt={RECOVERY_REASONS}
                        value={form.reason}
                        onChange={(value) => updateField("reason", value)}
                        className="w-full"
                        clearable={false}
                    />
                </div>

                <div className="mt-5">
                    <label className="block">
                        <span className="mb-2 block text-sm font-medium text-foreground">Supporting notes</span>
                        <textarea
                            value={form.notes}
                            onChange={(event) => updateField("notes", event.target.value)}
                            className="min-h-[140px] w-full rounded-2xl border border-lightBorder bg-white px-4 py-3 text-sm"
                            placeholder="Tell us what changed and any details that will help staff review your request."
                        />
                    </label>
                </div>

                {state.error ? (
                    <div className="mt-4">
                        <AuthAlert tone="error" title="Recovery request could not be submitted" live="assertive">
                            {state.error}
                        </AuthAlert>
                    </div>
                ) : null}

                <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row">
                    <SecondaryButton type="button" onClick={() => navigate(-1)} className="flex-1 text-lg" disabled={state.status === "submitting"}>
                        Back
                    </SecondaryButton>
                    <PrimaryButton type="submit" className="flex-1" disabled={!canSubmit} aria-busy={state.status === "submitting"}>
                        {state.status === "submitting" ? "Submitting request..." : "Submit recovery request"}
                    </PrimaryButton>
                </div>
            </form>
        </AuthShell>
    );
}
